import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, ChevronRight, Sprout, Trash2 } from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { API } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function FieldsPage() {
  const { t } = useLang();
  const nav = useNavigate();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", crop_name: "", planted_on: "" });
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/fields`);
      setFields(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/fields`, {
        name: form.name.trim(),
        crop_name: form.crop_name || null,
        planted_on: form.planted_on || null,
      });
      setFields([r.data, ...fields]);
      setForm({ name: "", crop_name: "", planted_on: "" });
      setShowForm(false);
      toast.success(t("fields.create"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/fields/${id}`);
      setFields(fields.filter((f) => f.id !== id));
      toast.success(t("fields.deleted"));
    } catch {
      toast.error("Failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="cd-heading text-3xl font-extrabold">{t("fields.title")}</h1>
            <p className="text-[#52796F] text-sm mt-1">{t("fields.sub")}</p>
          </div>
          <button
            data-testid="new-field-btn"
            onClick={() => setShowForm(!showForm)}
            className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> {t("fields.new")}
          </button>
        </div>

        {showForm && (
          <form
            onSubmit={create}
            data-testid="new-field-form"
            className="bg-white border border-[#E5E0D8] rounded-2xl p-5 mb-5 space-y-3 cd-fade-up"
          >
            <div>
              <label className="text-sm font-semibold">{t("fields.name")}</label>
              <input
                data-testid="field-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t("fields.name.placeholder")}
                required
                className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold">{t("fields.crop")} {t("common.optional")}</label>
                <input
                  data-testid="field-crop-input"
                  value={form.crop_name}
                  onChange={(e) => setForm({ ...form, crop_name: e.target.value })}
                  placeholder={t("fields.crop.placeholder")}
                  className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3"
                />
              </div>
              <div>
                <label className="text-sm font-semibold">{t("fields.plantedOn")} {t("common.optional")}</label>
                <input
                  data-testid="field-planted-input"
                  type="date"
                  value={form.planted_on}
                  onChange={(e) => setForm({ ...form, planted_on: e.target.value })}
                  className="mt-1 w-full bg-white border border-[#E5E0D8] rounded-xl px-4 py-3"
                />
              </div>
            </div>
            <button
              data-testid="create-field-btn"
              disabled={creating}
              className="bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-6 py-3 rounded-full font-bold inline-flex items-center gap-2"
            >
              {creating && <Loader2 className="w-4 h-4 animate-spin" />}
              {creating ? t("fields.creating") : t("fields.create")}
            </button>
          </form>
        )}

        {loading && (
          <div className="flex justify-center py-20" data-testid="fields-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#52796F]" />
          </div>
        )}

        {!loading && fields.length === 0 && (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-10 text-center" data-testid="fields-empty">
            <div className="w-14 h-14 bg-[#F4F1EA] rounded-2xl mx-auto flex items-center justify-center">
              <Sprout className="w-7 h-7 text-[#52796F]" />
            </div>
            <h3 className="cd-heading font-bold text-lg mt-4">{t("fields.empty")}</h3>
            <p className="text-sm text-[#52796F] mt-1">{t("fields.emptySub")}</p>
          </div>
        )}

        {!loading && fields.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="fields-list">
            {fields.map((f) => (
              <div
                key={f.id}
                data-testid={`field-card-${f.id}`}
                className="bg-white rounded-2xl border border-[#E5E0D8] p-5 cd-lift"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1 cursor-pointer" onClick={() => nav(`/fields/${f.id}`)}>
                    <h3 className="cd-heading font-bold text-lg truncate">{f.name}</h3>
                    <div className="text-xs text-[#52796F] mt-0.5">
                      {f.crop_name || "—"}
                      {f.planted_on && ` • ${new Date(f.planted_on).toLocaleDateString()}`}
                    </div>
                  </div>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        data-testid={`field-delete-btn-${f.id}`}
                        className="text-[#D90429] hover:bg-[#D90429]/10 p-2 rounded-lg"
                        aria-label="Delete field"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-white rounded-2xl">
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t("fields.deleteTitle")}</AlertDialogTitle>
                        <AlertDialogDescription>{t("fields.deleteDesc")}</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => remove(f.id)}
                          className="bg-[#D90429] hover:bg-[#a90220] text-white"
                        >
                          {t("common.delete")}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-[#52796F]">
                  <span>
                    {f.scan_count === 1
                      ? t("fields.scanCount", { n: f.scan_count })
                      : t("fields.scanCountPlural", { n: f.scan_count })}
                  </span>
                  {f.last_scan_at && <span>{new Date(f.last_scan_at).toLocaleDateString()}</span>}
                </div>
                <button
                  onClick={() => nav(`/fields/${f.id}`)}
                  data-testid={`open-field-${f.id}`}
                  className="mt-4 w-full bg-[#F4F1EA] hover:bg-[#E5E0D8] text-[#1B4332] py-2.5 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-1"
                >
                  {t("fields.openTimeline")} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
}
